import {
  CheckCircleFilled,
  CloseOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined
} from "@ant-design/icons"
import { Button, Checkbox, Flex, Tooltip, Typography, theme } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import {
  type CompanyInfoLike,
  type OnboardingItem,
  type OnboardingSubStep,
  type PersonalInfoLike,
  type TemplateSubsectionId,
  TEMPLATE_SUBSECTION_GUIDES
} from "../content/onboarding.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import {
  type TemplateCountKey,
  useTemplateCounts
} from "../../Settings/components/settings-templates/use-template-counts.ts"
import { useGuideOnboardingAssistant } from "./GuideOnboardingAssistantContext.tsx"

const { Title, Text } = Typography

/** Briefly rings the real field/section a sub-step points at, after
 * scrolling it into view — the same `data-guide-target` convention the
 * Product Tour already anchors on, so this never invents a second one. */
const focusTarget = (target: string) => {
  const el = document.querySelector<HTMLElement>(
    `[data-guide-target="${target}"]`
  )
  if (!el) return
  el.scrollIntoView({ behavior: "smooth", block: "center" })
  const previousOutline = el.style.outline
  const previousOutlineOffset = el.style.outlineOffset
  el.style.outline = "2px solid var(--ant-color-primary, #2A7D8C)"
  el.style.outlineOffset = "3px"
  window.setTimeout(() => {
    el.style.outline = previousOutline
    el.style.outlineOffset = previousOutlineOffset
  }, 1500)
}

interface RowState {
  subStep: OnboardingSubStep
  checked: boolean
  /** true = a personal "I did this" mark the user toggles by clicking the
   * checkbox (Level A, `GuideProgress.checkedGuideItemIds`); false = the row
   * reads a real signal (Level B) and its checkbox is display-only —
   * toggling it wouldn't change anything real, so it's rendered disabled. */
  manual: boolean
}

/** Where a sub-step has a real, already-computed signal to read (the exact
 * fields SettingsProfileForm/CompanyInformationFormSection validate,
 * `hasTeammates`, or a real template count), it reads that instead of the
 * manual mark — never both, per the two-level rule: guide-checklist state
 * must never fake business completion, and business completion must never
 * be hidden behind a manual mark that could disagree with it. Every other
 * row (no such signal exists — a photo, a logo, permissions/notifications
 * rows) is manual-only. */
const buildRows = (
  item: OnboardingItem,
  ctx: {
    userInfo: PersonalInfoLike | null | undefined
    companyInfo: CompanyInfoLike | null
    hasTeammates: boolean | null
    templateCounts: Partial<Record<TemplateCountKey, number>>
    isGuideItemChecked: (subStepId: string) => boolean
  }
): RowState[] => {
  if (!item.subSteps) return []
  return item.subSteps.map((subStep) => {
    if (item.id === "personal" && subStep.id === "basicInfo") {
      return {
        subStep,
        manual: false,
        checked: !!(ctx.userInfo?.first_name && ctx.userInfo?.last_name)
      }
    }
    if (item.id === "personal" && subStep.id === "contactInfo") {
      return {
        subStep,
        manual: false,
        checked: !!(ctx.userInfo?.email && ctx.userInfo?.phone)
      }
    }
    if (item.id === "company" && subStep.id === "companyDetails") {
      return {
        subStep,
        manual: false,
        checked: !!(
          ctx.companyInfo?.business_name && ctx.companyInfo?.company_phone
        )
      }
    }
    if (item.id === "company" && subStep.id === "licenseInformation") {
      return {
        subStep,
        manual: false,
        checked: !!ctx.companyInfo?.license_number
      }
    }
    if (item.id === "team" && subStep.id === "addTeamMembers") {
      return { subStep, manual: false, checked: !!ctx.hasTeammates }
    }
    if (item.id === "templates") {
      // Unlike Personal/Company/Team, a Templates row stays manually
      // toggleable even once a real count resolves — the overall step's
      // real completion is governed separately, by `hasProjectTypes` on the
      // item itself, never by these per-row marks. A real, positive count
      // still checks the box on its own (never uncheckable down to "no
      // template" by mistake), but visiting the area and returning via
      // "Back to Templates" is allowed to mark REVIEWED even at zero — this
      // checklist means "I looked at this," never "an object exists."
      const count = ctx.templateCounts[subStep.id as TemplateCountKey]
      const manuallyChecked = ctx.isGuideItemChecked(subStep.id)
      return {
        subStep,
        manual: true,
        checked: (count != null && count > 0) || manuallyChecked
      }
    }
    return {
      subStep,
      manual: true,
      checked: ctx.isGuideItemChecked(subStep.id)
    }
  })
}

const ChecklistRow = ({
  subStep,
  checked,
  manual,
  onToggle,
  onView
}: {
  subStep: OnboardingSubStep
  checked: boolean
  manual: boolean
  onToggle: () => void
  /** Overrides the default target-scroll/route-navigate View action —
   * Templates rows use this to open the area's contextual guide (real
   * navigation + the panel switching state) instead of a bare navigate. */
  onView?: () => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [targetExists, setTargetExists] = React.useState(false)

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- the target is looked up in the DOM after every render because nothing in React says when the page under the panel mounts it
  React.useEffect(() => {
    if (!subStep.target) {
      setTargetExists(false)
      return
    }
    setTargetExists(
      !!document.querySelector(`[data-guide-target="${subStep.target}"]`)
    )
  })

  const showView = onView ? true : subStep.route ? true : targetExists

  return (
    <Flex
      align="flex-start"
      justify="space-between"
      gap={8}
      className="rounded-md px-2 py-2 -mx-2 transition-colors hover:bg-black/[0.03]"
    >
      <Flex align="flex-start" gap={10} className="min-w-0 flex-1">
        <Checkbox
          checked={checked}
          disabled={!manual}
          onChange={manual ? onToggle : undefined}
          className="mt-0.5"
          aria-label={t(subStep.labelKey)}
        />
        <Flex vertical gap={0} className="min-w-0 flex-1">
          <Text
            className="text-sm font-medium"
            type={checked ? "secondary" : undefined}
          >
            {t(subStep.labelKey)}
          </Text>
          {subStep.descriptionKey && (
            <Text type="secondary" className="text-xs">
              {t(subStep.descriptionKey)}
            </Text>
          )}
        </Flex>
      </Flex>
      {showView && (
        <Button
          type="link"
          size="small"
          className="!h-auto shrink-0 !px-0 !text-xs"
          onClick={
            onView ??
            (() =>
              subStep.route
                ? navigate(subStep.route)
                : focusTarget(subStep.target!))
          }
        >
          {t("guide.setupAssistant.viewAction")} →
        </Button>
      )}
    </Flex>
  )
}

const HelperCallout = ({ text }: { text: string }) => {
  const { token } = theme.useToken()
  return (
    <div
      className="mt-3 rounded-md px-3 py-2.5 text-xs leading-relaxed"
      style={{
        background: token.colorFillTertiary,
        color: token.colorTextSecondary
      }}
    >
      {text}
    </div>
  )
}

/** A subtle "✓ X reviewed" line once every visible row is checked — purely
 * cosmetic feedback on the guide checklist (Level A + whichever rows read a
 * real signal), never a claim about strict form validation. We are tracking
 * onboarding progress here, not requiring every optional field to be
 * filled in. */
const ChecklistCompleteBanner = ({ item }: { item: OnboardingItem }) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  return (
    <Flex align="center" gap={6} className="mt-3">
      <CheckCircleFilled
        className="text-sm"
        style={{ color: token.colorPrimary }}
      />
      <Text
        className="text-sm font-medium"
        style={{ color: token.colorPrimary }}
      >
        {t("guide.setupAssistant.checklistComplete", {
          step: t(item.labelKey)
        })}
      </Text>
    </Flex>
  )
}

/** The universal checklist body — every step (Personal, Company, Team,
 * Permissions, Notifications, Templates) renders the same way: a short
 * "why it matters" line, an optional helper callout, the checklist itself,
 * a completion banner once every row is checked, then whatever primary
 * action that step type needs. One shared component for all six — only the
 * data a row's checkbox reads (`buildRows`) varies per step. Personal and
 * Company get no separate "go to this page" button — `activeItem` is only
 * ever set once `location.pathname` already matches the step's own page
 * (see `GuideOnboardingAssistantContext.goToIndex`), so it would always be
 * redundant with the page already showing. */
const StepBody = ({ item }: { item: OnboardingItem }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    signals,
    markReviewed,
    isGuideItemChecked,
    toggleGuideItem,
    openTemplatesSubsection
  } = useGuideOnboardingAssistant()
  const userInfo = useAppSelector((state) => state.auth?.userInfo)
  const templatePermissions = useAppSelector(
    (state) => state.auth?.permissions?.codes?.settings
  )
  const { counts } = useTemplateCounts(
    item.id === "templates" ? templatePermissions : undefined
  )

  const templateCounts: Partial<Record<TemplateCountKey, number>> = {}
  if (item.id === "templates") {
    for (const key of Object.keys(counts) as TemplateCountKey[]) {
      const state = counts[key]
      if (state.status === "ready") templateCounts[key] = state.count
    }
  }

  const rows = buildRows(item, {
    userInfo,
    companyInfo: signals.companyInfo,
    hasTeammates: signals.hasTeammates,
    templateCounts,
    isGuideItemChecked
  })
  const allChecked = rows.length > 0 && rows.every((row) => row.checked)

  return (
    <>
      <Text type="secondary" className="text-sm">
        {t(`guide.setupAssistant.${item.id}.body`)}
      </Text>
      {item.helperCalloutKey && (
        <HelperCallout text={t(item.helperCalloutKey)} />
      )}
      {rows.length > 0 && (
        <Flex vertical className="mt-3">
          {rows.map((row) => (
            <ChecklistRow
              key={row.subStep.id}
              subStep={row.subStep}
              checked={row.checked}
              manual={row.manual}
              onToggle={() => toggleGuideItem(row.subStep.id)}
              onView={
                item.id === "templates"
                  ? () =>
                      openTemplatesSubsection(
                        row.subStep.id as TemplateSubsectionId
                      )
                  : undefined
              }
            />
          ))}
        </Flex>
      )}
      {allChecked && <ChecklistCompleteBanner item={item} />}
      {item.id === "team" && (
        <Button
          type="primary"
          block
          icon={<PlusOutlined />}
          className="mt-4"
          onClick={() => navigate(`${item.linkTo}?addMember=1`)}
        >
          {t("guide.setupAssistant.team.addTeamMember")}
        </Button>
      )}
      {(item.id === "permissions" || item.id === "notifications") && (
        <Button
          type="primary"
          block
          className="mt-4"
          onClick={() => markReviewed(item.id)}
        >
          {t(`guide.setupAssistant.${item.id}.reviewedCta`)}
        </Button>
      )}
    </>
  )
}

/** Nested within the Templates step (never a 7th step of its own) — shown
 * once a checklist row's "View →" has navigated to that area's real page.
 * Its own checklist is guide-only (Level A, "I reviewed this"), namespaced
 * `templates.<area>.<item>` so it can never collide with the main
 * checklist's own `templates.<area>` row keys. This is guidance, not a
 * wizard: every item here is optional, and "Back to Templates" is always
 * available regardless of what's checked. */
const TemplatesSubsectionBody = ({
  subsectionId
}: {
  subsectionId: TemplateSubsectionId
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isGuideItemChecked, toggleGuideItem } = useGuideOnboardingAssistant()
  const guide = TEMPLATE_SUBSECTION_GUIDES[subsectionId]

  // Document Templates has a real, separately-routed "create" screen — used
  // directly rather than reaching into the list page's own state. The other
  // three areas create through an on-page modal with no route of its own,
  // so their primary action stays on the same real page the row already
  // opened (never a redundant "go here" for a page already showing).
  const handlePrimaryAction = () => {
    navigate(
      guide.id === "documents" ? RoutesPaths.documentTemplates.add : guide.route
    )
  }

  return (
    <>
      <Text type="secondary" className="text-sm">
        {t(guide.bodyKey)}
      </Text>
      {guide.exampleKeys.length > 0 && (
        <Flex vertical gap={2} className="mt-3">
          <Text type="secondary" className="text-xs">
            {t(guide.exampleLabelKey)}
          </Text>
          {guide.exampleKeys.map((key) => (
            <Text key={key} type="secondary" className="text-xs">
              • {t(key)}
            </Text>
          ))}
        </Flex>
      )}
      {guide.helperKey && <HelperCallout text={t(guide.helperKey)} />}

      <Text
        className="mt-4 block text-xs font-semibold uppercase tracking-wide"
        type="secondary"
      >
        {t(guide.checklistTitleKey)}
      </Text>
      <Flex vertical className="mt-1">
        {guide.checklist.map((checklistItem) => {
          const key = `${guide.id}.${checklistItem.id}`
          const checked = isGuideItemChecked(key)
          return (
            <Flex
              key={checklistItem.id}
              align="flex-start"
              gap={10}
              className="py-1.5"
            >
              <Checkbox
                checked={checked}
                onChange={() => toggleGuideItem(key)}
                className="mt-0.5"
                aria-label={t(checklistItem.labelKey)}
              />
              <Text
                className="text-sm"
                type={checked ? "secondary" : undefined}
              >
                {t(checklistItem.labelKey)}
              </Text>
            </Flex>
          )
        })}
      </Flex>

      <Button
        type="primary"
        block
        className="mt-4"
        onClick={handlePrimaryAction}
      >
        {t(guide.ctaKey)}
      </Button>
    </>
  )
}

/**
 * A real wizard, not a locked sequence — every visible setup step is
 * reachable from every other one via the footer (Back/Skip/Next) or the
 * main Guide Home stepper, regardless of completion state. Navigating
 * NEVER marks a step complete on its own; only its own real completion
 * signal (or, for REVIEW steps, an explicit "I've reviewed" click) does.
 * Any unsaved edit on the real Settings page this floats over is caught by
 * that page's own existing `ReactRouterPrompt`/`usePrompt` blocker — every
 * navigation here goes through real `react-router-dom` `navigate()` calls,
 * so it's protected the same way any other in-app link already is, with no
 * second unsaved-changes mechanism built here.
 *
 * Layout: header, checklist and footer all live in ONE scrollable region —
 * the footer is `sticky bottom-0` inside it rather than a separate flex
 * sibling with a fixed reserved height. A short checklist (Permissions,
 * Notifications) leaves the footer sitting right below the content, exactly
 * where it naturally falls; a long one (Templates' four rows plus a helper
 * callout) scrolls the region and the footer sticks to the bottom of the
 * visible viewport once it would otherwise scroll away. Either way Back/
 * Skip/Next are never more than one screen's height from the content.
 */
export const GuideSetupAssistantPanel = () => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const {
    activeItem,
    activeIndex,
    items,
    closeAssistant,
    back,
    next,
    skipStep,
    templatesSubsection,
    backToTemplates
  } = useGuideOnboardingAssistant()

  if (!activeItem) return null

  const isFirst = activeIndex <= 0
  const isLast = activeIndex >= items.length - 1
  const inTemplatesSubsection =
    activeItem.id === "templates" && !!templatesSubsection

  return (
    <div
      className="h-full w-96 shrink-0 overflow-y-auto border-l"
      style={{
        borderColor: token.colorBorderSecondary,
        background: token.colorBgContainer
      }}
    >
      <div className="px-5 py-4">
        <Flex justify="space-between" align="center">
          <Text
            className="text-xs font-semibold uppercase tracking-wide"
            type="secondary"
          >
            {t("guide.setupAssistant.eyebrow")}
          </Text>
          <Tooltip title={t("guide.setupAssistant.close")}>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={closeAssistant}
              aria-label={t("guide.setupAssistant.close")}
            />
          </Tooltip>
        </Flex>

        {inTemplatesSubsection ? (
          <>
            {/* No "Step X of 6" here — a subsection is nested inside
                Templates, never a step of its own. */}
            <Text type="secondary" className="text-xs">
              {t("guide.setupAssistant.templates.subsectionEyebrow")}
            </Text>
            <Button
              type="link"
              size="small"
              icon={<LeftOutlined />}
              className="!mt-1 !h-auto !px-0 !text-xs"
              onClick={backToTemplates}
            >
              {t("guide.setupAssistant.templates.backToTemplates")}
            </Button>

            <Title level={4} className="!mb-1 !mt-3 !text-xl">
              {t(TEMPLATE_SUBSECTION_GUIDES[templatesSubsection!].titleKey)}
            </Title>

            <TemplatesSubsectionBody subsectionId={templatesSubsection!} />
          </>
        ) : (
          <>
            <Text type="secondary" className="text-xs">
              {t("guide.setupAssistant.stepCounter", {
                current: activeIndex + 1,
                total: items.length
              })}
            </Text>

            <Title level={4} className="!mb-1 !mt-3 !text-xl">
              {t(`guide.setupAssistant.${activeItem.id}.title`)}
            </Title>

            <StepBody item={activeItem} />
          </>
        )}
      </div>

      <Flex
        justify={inTemplatesSubsection ? "start" : "space-between"}
        align="center"
        gap={8}
        className="sticky bottom-0 min-h-[64px] border-t px-5 py-3"
        style={{
          borderColor: token.colorBorderSecondary,
          background: token.colorBgContainer,
          boxShadow: "0 -2px 8px rgba(15, 23, 42, 0.05)"
        }}
      >
        {inTemplatesSubsection ? (
          <Button
            size="small"
            icon={<LeftOutlined />}
            onClick={backToTemplates}
          >
            {t("guide.setupAssistant.templates.backToTemplates")}
          </Button>
        ) : (
          <>
            {isFirst ? (
              <div />
            ) : (
              <Button size="small" icon={<LeftOutlined />} onClick={back}>
                {t("guide.setupAssistant.back")}
              </Button>
            )}
            <Flex gap={8}>
              <Button
                type="text"
                size="small"
                onClick={() => skipStep(activeItem.id)}
              >
                {t("guide.setupAssistant.skip")}
              </Button>
              <Button
                type="primary"
                size="small"
                icon={isLast ? undefined : <RightOutlined />}
                iconPosition="end"
                onClick={next}
              >
                {isLast
                  ? t("guide.setupAssistant.finishSetup")
                  : t("guide.setupAssistant.next")}
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </div>
  )
}
